/* @flow */
import React, { useContext } from 'react';
import get from 'lodash.get';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import { Net } from './Net';
import { Sale } from './Sale';
import { Retail } from './Retail';

// Helpers
import { textPriceValue, types, isValidTextPriceType } from '../helpers/priceTypeConstants';
import { TileContext } from '../helpers/TileContext';

import type { Price_item as PriceItemProp } from './__generated__/Price_item.graphql';

// Styles
import styles from './styles.scss';

type PriceProps = {|
    currency: string,
    item: PriceItemProp,
    hidePercentageOff?: boolean,
    priceBold: boolean,
|};

export const PriceComponent = ({ currency, item, hidePercentageOff, priceBold }: PriceProps) => {
    const { imageSize } = useContext(TileContext);
    const displayPrice = get(item, 'displayPrice.0');
    // Price display for SB page type always returns a single price type
    const {
        type,
        convertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        showPriceVariability,
    } = displayPrice;
    const percentageValue = hidePercentageOff ? null : percentageOff;

    let priceClass = classnames(
        styles.price,
        {
            [styles.sold]: type === types.SOLD,
            [styles.hold]: type === types.HOLD,
        },
        styles[imageSize]
    );

    if (isValidTextPriceType(type)) {
        return (
            <div className={priceClass} data-tn={`price-${type}`}>
                {textPriceValue(type)}
            </div>
        );
    }

    const amountByCurrency =
        (convertedAmountList || []).find(
            convertedAmount => convertedAmount.currency === currency
        ) || {};
    const price = Math.floor(amountByCurrency.amount) || 0;

    const sharedProps = {
        currency,
        price,
        quantityDisplay,
        showPriceVariability,
    };

    // priceBold prop should only apply to actual prices
    priceClass = classnames(priceClass, {
        [styles.priceBold]: priceBold,
    });

    if (type === types.SALE) {
        return (
            <div className={priceClass} data-tn="price-sale">
                <Sale {...sharedProps} percentageOff={percentageValue} />
            </div>
        );
    } else if (type === types.NET) {
        return (
            <div className={priceClass} data-tn="price-net">
                <Net {...sharedProps} percentageOff={percentageValue} />
            </div>
        );
    } else if (type === types.RETAIL) {
        return (
            <div className={priceClass} data-tn="price-retail">
                <Retail {...sharedProps} showLabel={showPriceLabel} />
            </div>
        );
    }
    return null;
};

PriceComponent.defaultProp = {
    currency: 'USD',
};

export const Price = createFragmentContainer(PriceComponent, {
    item: graphql`
        fragment Price_item on Item
            @argumentDefinitions(
                isTrade: { type: "Boolean", defaultValue: false }
                pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
            ) {
            displayPrice(page: $pageDisplayEnum, isTrade: $isTrade) {
                convertedAmountList {
                    amount
                    currency
                }
                quantityDisplay
                percentageOff
                type
                showPriceLabel
                showPriceVariability
            }
        }
    `,
});
